import { STATIC_ASSET_URL, GOOGLE_API_KEY } from '../../config';
import { getIOSVersion } from '../AppInfo';

/**
 * Creates an address string encoding that can be used with the Google or Apple map API
 *
 * @param {Object} profile - The profile containing the address information
 * @param {string} profile.address1 - Line 1 of the address, usually containing number and street
 * @param {string} profile.address2 - Line 2 of the address, optional
 * @param {string} profile.city
 * @param {string} profile.state
 * @param {string} profile.zipcode
 */
export const getAddressString = profile => {
  if (!profile || !profile.zipcode) return null;

  return [
    profile.address1,
    profile.address2,
    profile.city,
    profile.state,
    profile.zipcode,
  ].filter(item => !!item).join(', ');
};

/**
 * Generates a link to Google (or Apple) maps at the location specified
 * by the parameter's address information
 *
 * @param {Object} profile - The profile containing the address information
 * @param {string} profile.address1 - Line 1 of the address, usually containing number and street
 * @param {string} profile.address2 - Line 2 of the address, optional
 * @param {string} profile.city
 * @param {string} profile.state
 * @param {string} profile.zipcode
 */
export const googleMapURL = profile => {
  const address = getAddressString(profile);
  if (!address) return null;

  if (getIOSVersion()) {
    return `https://maps.apple.com/?q=${encodeURIComponent(address)}`;
  }
  return `https://maps.google.com/maps?q=${encodeURIComponent(address)}`;
};

/**
 * Generates the URI of a Google Maps image containing a pin at the given location
 *
 * @param {Object} profile - The profile containing the address information
 * @param {string} profile.address1 - Line 1 of the address, usually containing number and street
 * @param {string} profile.address2 - Line 2 of the address, optional
 * @param {string} profile.city
 * @param {string} profile.state
 * @param {string} profile.zipcode
 */
export const getStaticGoogleMapsUrl = function (provider, options) {
  options = {
    height: 640,
    width: 640,
    format: 'png',
    scale: 2,
    iconScale: 2,
    maptype: 'roadmap',
    ...options,
  };

  if (options.icon === undefined) {
    // eslint-disable-next-line eqeqeq
    if (options.iconScale == 1) {
      options.icon = `${STATIC_ASSET_URL}/misc/icon-map-marker.b377e7a687acaa44efe0c21da3af8fba9675363f.png`;
      // eslint-disable-next-line eqeqeq
    } else if (options.iconScale == 2) {
      options.icon = `${STATIC_ASSET_URL}/misc/icon-map-marker-2x.229e3e0f22f5d33d01074857f5e0ca530165f9be.png`;
    }
  }

  if (options.icon && options.icon.indexOf('https:') === 0) {
    // Google doesn't like https:// icons
    options.icon = options.icon.replace(/^https/, 'http');
  }

  const size = `${options.width}x${options.height}`;
  const markers = (options.icon ? (`scale:${options.iconScale}|icon:${options.icon}|`) : '') + getAddressString(provider);
  return `https://maps.googleapis.com/maps/api/staticmap?size=${encodeURIComponent(size)}&markers=${encodeURIComponent(markers)}&format=${encodeURIComponent(options.format)}&scale=${encodeURIComponent(options.scale)}&maptype=${encodeURIComponent(options.maptype)}&key=${GOOGLE_API_KEY}`;
};
